import { useState } from "react";
import { useGlobalSearchParams } from "expo-router";
import { ArrowDown, Button, ButtonText, Table, XInCircle } from "@ptsd/uikit";
import { ArrowUpDown } from "@tamagui/lucide-icons";
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import type { SortingState } from "@tanstack/react-table";
import type { PaginationState } from "@tanstack/table-core";
import { isValid as isValidDate } from "date-fns";
import { ScrollView, Text, View, XStack } from "tamagui";

import { api } from "~/utils/api";
import { toReadableDMY } from "~/utils/date";
import { Pagination } from "~/components/blocks";

const LIMIT = 16;

type Participant = Record<string, string | Date | null> & {
  patientUserId: string | null;
};

const resolveCellWidth = (cellId: string) => {
  switch (cellId) {
    case "patientUserId":
      return 200;
    case "phone":
      return 150;
    case "email":
      return 300;
    case "groupName":
      return 300;
    case "delete":
      return 200;
    default:
      return 200;
  }
};

const columnHelper = createColumnHelper<Participant>();

export const ParticipantsTable = () => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const { researchId } = useGlobalSearchParams<{
    researchId: string;
  }>();
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: LIMIT,
  });

  const apiUtils = api.useUtils();

  const [data] = api.research.participants.useSuspenseQuery({
    limit: pagination.pageSize,
    offset: pagination.pageSize * pagination.pageIndex,
    researchId,
    orderBy: sorting[0]?.id,
    orderDirection: sorting[0]?.desc ? "desc" : "asc",
  });

  const { mutate } = api.research.removeParticipants.useMutation({
    onSuccess: () => apiUtils.research.participants.invalidate(),
  });

  const baseColumns = data.columns.map((col) =>
    columnHelper.accessor(col.key, {
      header: (info) => {
        const sortState = info.column.getIsSorted();

        if (col.key === "patientUserId") {
          return (
            <Text
              fontFamily={"$subtitle"}
              fontSize={"$2"}
              lineHeight={"$2"}
              fontWeight={"$5"}
              color={"$gray-primary-900"}
              width={"100%"}
              textAlign="left"
            >
              {col.name}
            </Text>
          );
        }
        return (
          <XStack
            width={"100%"}
            alignItems="center"
            justifyContent="flex-start"
          >
            <Text
              fontFamily={"$subtitle"}
              fontSize={"$2"}
              lineHeight={"$2"}
              fontWeight={"$5"}
              color={"$gray-primary-900"}
              width={"100%"}
              textAlign="left"
            >
              {col.name}
            </Text>
            <Button
              onPress={(e) => {
                e.preventDefault();
                info.column.toggleSorting(sortState === "asc");
              }}
              padding={0}
              mode="icon"
              variant={"transparent"}
            >
              {sortState === false ? (
                <ArrowUpDown />
              ) : (
                <View
                  style={{
                    rotate: sortState === "asc" ? "180deg" : "0deg",
                  }}
                >
                  <ArrowDown />
                </View>
              )}
            </Button>
          </XStack>
        );
      },
      cell: (info) => {
        const value = info.getValue();
        const key = col.key;
        const notDefined =
          value === null || value === undefined || value === "";

        if (notDefined && key === "phone") {
          return "Не вказаний";
        }

        if (isValidDate(value)) {
          return toReadableDMY(value);
        }
        if (notDefined) {
          return "Не пройшов";
        }
        return value;
      },
    }),
  );

  const getColumnsWithDeleteAction = ({
    onDelete,
    deletionDisabled,
  }: {
    onDelete: (id: string) => void;
    deletionDisabled: boolean;
  }) => {
    return [
      ...baseColumns,
      columnHelper.accessor((row) => ({ id: row.id }), {
        header: () => {
          return (
            <Text
              fontFamily={"$subtitle"}
              fontSize={"$2"}
              lineHeight={"$2"}
              fontWeight={"$5"}
              color={"$gray-primary-900"}
              width={"100%"}
              textAlign="left"
            >
              Керування
            </Text>
          );
        },
        cell: ({ row }) => {
          return (
            <Button
              width={"100%"}
              height={"$4"}
              variant={"danger"}
              size={"sm"}
              maxWidth={235}
              rounded="lg"
              disabled={deletionDisabled}
              disabledStyle={{
                borderColor: "$gray-200",
                borderWidth: "$0.2",
              }}
              onPress={() => onDelete(row.original.patientUserId ?? "")}
              //NOTE: used hex coz it is not tamagui icon
            >
              <XStack gap={"$0.5"}>
                <XInCircle
                  color={"#D6453B"}
                  {...(deletionDisabled && {
                    opacity: 0.3,
                    color: "#CDD3DC",
                  })}
                />
                <ButtonText
                  disabled={deletionDisabled}
                  variant={"danger"}
                  font={"sm"}
                  disabledStyle={{
                    color: "$gray-200",
                  }}
                >
                  Видалити
                </ButtonText>
              </XStack>
            </Button>
          );
        },
        enableSorting: false,
        enableHiding: false,
        id: "delete",
      }),
    ];
  };

  const table = useReactTable({
    data: data.rows,
    columns: getColumnsWithDeleteAction({
      deletionDisabled: data.deletionDisabled,
      onDelete: (id) => {
        mutate({
          researchId,
          ids: [id],
        });
      },
    }),
    getCoreRowModel: getCoreRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    manualSorting: true,
    rowCount: data.totalRows,
    state: {
      pagination,
      sorting,
    },
    onSortingChange: setSorting,
  });

  const headerGroups = table.getHeaderGroups();
  const tableRows = table.getRowModel().rows;

  const pageIndex = table.getState().pagination.pageIndex;
  const totalPages = table.getPageCount();

  return (
    <>
      <ScrollView
        flex={1}
        width={"100%"}
        horizontal
        scrollEventThrottle={12}
        contentContainerStyle={{ flexDirection: "row" }}
      >
        <Table
          alignCells={{ x: "center", y: "center" }}
          alignHeaderCells={{ y: "center", x: "center" }}
          cellHeight="$5.8"
        >
          <Table.Head data-id="users-table-head">
            {headerGroups.map((headerGroup, index) => {
              return (
                <Table.Row
                  data-id="users-table-header-row"
                  rowLocation={
                    index === 0
                      ? "first"
                      : index === headerGroups.length - 1
                        ? "last"
                        : "middle"
                  }
                  key={headerGroup.id}
                  justifyContent="flex-start"
                >
                  {headerGroup.headers.map((header, index) => {
                    return (
                      <Table.HeaderCell
                        data-id={"users-table-header-cell-" + header.id}
                        backgroundColor="white"
                        key={header.id}
                        justifyContent="flex-start"
                        borderWidth={0}
                        cellLocation={
                          index === 0
                            ? "first"
                            : index === headerGroup.headers.length - 1
                              ? "last"
                              : "middle"
                        }
                        width={resolveCellWidth(header.id)}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                      </Table.HeaderCell>
                    );
                  })}
                </Table.Row>
              );
            })}
          </Table.Head>

          <Table.Body data-id="users-table-body">
            {tableRows.length === 0 ? (
              <Table.Row
                data-id="users-table-row"
                borderBottomRightRadius={"$1"}
                borderBottomLeftRadius={"$1"}
                backgroundColor={"white"}
                key={1}
              >
                <Table.Cell
                  data-id="users-table-no-data-cell"
                  borderColor={"$gray-200"}
                  width={"100%"}
                  key={1}
                  justifyContent="center"
                >
                  <Text
                    numberOfLines={1}
                    ellipsizeMode="tail"
                    fontFamily={"$subtitle"}
                    fontSize={"$2"}
                    lineHeight={"$2"}
                    color={"$gray-primary-900"}
                  >
                    Немає даних
                  </Text>
                </Table.Cell>
              </Table.Row>
            ) : (
              tableRows?.map((row, index) => {
                return (
                  <Table.Row
                    data-id="users-table-row"
                    backgroundColor={index % 2 === 0 ? "white" : "$gray-50"}
                    selected={row.getIsSelected()}
                    rowLocation={
                      index === 0
                        ? "first"
                        : index === tableRows.length - 1
                          ? "last"
                          : "middle"
                    }
                    key={row.id}
                  >
                    {row.getVisibleCells().map((cell, index) => {
                      return (
                        <Table.Cell
                          data-id={"users-table-cell-" + cell.id}
                          justifyContent="flex-start"
                          cellLocation={
                            index === 0
                              ? "first"
                              : row.getVisibleCells().length - 1 === index
                                ? "last"
                                : "middle"
                          }
                          key={cell.id}
                          flex={1}
                          width={resolveCellWidth(cell.column.id)}
                        >
                          <Text
                            width={"100%"}
                            fontFamily={"$subtitle"}
                            lineHeight={"$2"}
                            fontSize={"$2"}
                            fontWeight={"$4"}
                            color={"$gray-primary-900"}
                          >
                            {flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )}
                          </Text>
                        </Table.Cell>
                      );
                    })}
                  </Table.Row>
                );
              })
            )}
          </Table.Body>
        </Table>
      </ScrollView>

      <Pagination
        currentPage={pageIndex + 1}
        onPageChange={(page) => {
          table.setPageIndex(page - 1);
        }}
        totalPages={totalPages}
        paddingTop={"$3"}
      />
    </>
  );
};
